import {
  Box,
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Row,
  Space,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { isBefore2025 as isBefore2025Func } from '@/features/project/_utils/project';
import {
  CorporateFormLabelModifier,
  FundAdditionalAttributesModel,
  FundSheetModel,
} from '@/features/project/projects/_models/project';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { KsSheetTypeMetaInformation } from './KsSheetTypeMetaInformation';
import { ProjectInformationHeader } from './ProjectInformationHeader';
import {
  KsBusinessFormPinpointItem,
  KsFundNecessityReasonsPinpointItem,
  KsFundNecessityTypePinpointItem,
  KsOperationFormPinpointItem,
  ProjectGroupPinpointItem,
  ProjectLegalBasesPinpointItem,
  ProjectNumberPinpointItem,
  ProjectOldProjectNumberPinpointItem,
  ProjectOverviewPinpointItem,
  ProjectPeriodPinpointItem,
  ProjectPlanPinpointItem,
  ProjectPurposePinpointItem,
  ProjectRelatedProjectsPinpointItem,
  ProjectRemarkPinpointItem,
  ProjectTaskPinpointItem,
} from './pinpoint-items/basic-information-pinpoint-fields';

type Props = z.infer<typeof FundSheetModel> & {
  historySavedAt: string | undefined;
};

const PINPOINT_SHEET_TYPE = 'KS_STANDARD';

export const KsStandardProjectInformation: FC<Props> = (props) => {
  const {
    id,
    fiscalYear,
    relatedProjects,
    projectNumber,
    oldProjectNumber,
    startYear,
    endYear,
    purpose,
    overview,
    overviewUrl,
    legalBases,
    plans,
    task,
    remark,
    name,
    inspectionEvaluation,
    isUnknownStart,
    isNoScheduledEnd,
    ...rest
  } = FundSheetModel.parse(props);
  const parsedFundAdditionalAttributes = FundAdditionalAttributesModel.parse(
    props.fundAdditionalAttributes,
  );

  const { t } = useTranslation();

  const isBefore2025 = useMemo(
    () => isBefore2025Func({ fiscalYear }),
    [fiscalYear],
  );

  const isOptionalCorporateForm =
    rest.fundAdditionalAttributes?.corporateForm === 'others';

  return (
    <>
      <ProjectInformationHeader
        ministryId={rest.ministryId}
        historySavedAt={props.historySavedAt}
        organization={rest.organization}
        relatedOrganizations={rest.relatedOrganizations}
        otherResponsibleOrganizations={rest.otherResponsibleOrganizations}
        businessManagers={rest.businessManagers}
        peopleInCharge={rest.peopleInCharge}
        snapshotId={rest.snapshotId}
        pinpointSheetType="KS_STANDARD"
        additionalJurisdictionalUsers={
          rest.fundAdditionalAttributes?.additionalJurisdictionalUsers
        }
        additionalJurisdictionalOrganizations={
          rest.additionalJurisdictionalOrganizations
        }
        additionalJurisdictionalBusinessManagers={
          rest.additionalJurisdictionalBusinessManagers
        }
        additionalJurisdictionalPeopleInCharge={
          rest.additionalJurisdictionalPeopleInCharge
        }
      />

      <ContentWrapper title={t('basic-information')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectNumberPinpointItem
                key="project-number"
                projectNumber={projectNumber}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                projectId={id}
              />,
              <ProjectGroupPinpointItem
                key="project-group"
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
              />,
            ]}
          />
        </ContentItem>

        <Flex vertical gap={4}>
          {rest.fundAdditionalAttributes && (
            <KsSheetTypeMetaInformation
              isBefore2025={isBefore2025}
              fundSheetType={rest.fundAdditionalAttributes.fundSheetType}
              fundSheetNumber={rest.fundAdditionalAttributes.fundSheetNumber}
              fundSheetBranchNumber={
                rest.fundAdditionalAttributes.fundSheetBranchNumber
              }
            />
          )}
          <Flex vertical>
            <Text variant="h4" text={name} weight="bold" />
            <Text
              variant="h5"
              text={rest.fundAdditionalAttributes?.fundBusinessName}
            />
          </Flex>
        </Flex>

        <ContentItem paddingBottom={0} borderBottom={0}>
          <Row gutter={[24, 24]}>
            <Col span={6}>
              <PinpointMarker
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType: PINPOINT_SHEET_TYPE,
                  ksStandard: {
                    field: 'ks.standard.fundOwner',
                    path: { project: id },
                  },
                })}
              >
                <TitledText
                  title={t('fund-owner')}
                  text={rest.fundAdditionalAttributes?.fundOwner}
                />
              </PinpointMarker>
            </Col>
            <Col span={6}>
              <PinpointMarker
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType: PINPOINT_SHEET_TYPE,
                  ksStandard: {
                    field: isOptionalCorporateForm
                      ? 'ks.standard.optional-corporate-form'
                      : 'ks.standard.corporate-form',
                    path: { project: id },
                  },
                })}
              >
                <TitledText
                  title={
                    isOptionalCorporateForm
                      ? t('optional-corporate-form')
                      : t('corporate-form')
                  }
                  text={
                    isOptionalCorporateForm
                      ? rest.fundAdditionalAttributes?.optionalCorporateForm
                      : rest.fundAdditionalAttributes?.corporateForm
                        ? CorporateFormLabelModifier[
                            rest.fundAdditionalAttributes.corporateForm
                          ]
                        : null
                  }
                />
              </PinpointMarker>
            </Col>
            {!isBefore2025 && (
              <>
                <Col span={12}>
                  <PinpointMarker
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field:
                          'ks.standard.eligibility-inspection-method-and-reasons-for-selection',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t(
                        'eligibility-inspection-method-and-reasons-for-selection',
                      )}
                      text={
                        inspectionEvaluation.eligibilityInspection
                          ?.methodAndReasonsForSelection
                      }
                    />
                  </PinpointMarker>
                </Col>
                <Col span={12}>
                  <PinpointMarker
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field: 'ks.standard.eligibility-inspection-results',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t('eligibility-inspection-results')}
                      text={inspectionEvaluation.eligibilityInspection?.results}
                    />
                  </PinpointMarker>
                </Col>
                <Col span={12}>
                  <PinpointMarker
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field:
                          'ks.standard.implementation-status-of-inspection',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t('implementation-status-of-inspection')}
                      text={
                        inspectionEvaluation.implementationStatusOfInspection
                      }
                    />
                  </PinpointMarker>
                </Col>
              </>
            )}
            {isBefore2025 && (
              <Col span={12}>
                <ProjectPeriodPinpointItem
                  key="period"
                  projectId={id}
                  pinpointSheetType={PINPOINT_SHEET_TYPE}
                  startYear={startYear}
                  endYear={endYear}
                  isUnknownStart={isUnknownStart}
                  isNoScheduledEnd={isNoScheduledEnd}
                />
              </Col>
            )}
            <Col span={6}>
              <KsOperationFormPinpointItem
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                operationForms={rest.fundAdditionalAttributes?.operationForms}
              />
            </Col>
            <Col span={6}>
              <KsBusinessFormPinpointItem
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                businessForms={rest.fundAdditionalAttributes?.businessForms}
              />
            </Col>
            <Col span={12}>
              <ProjectRelatedProjectsPinpointItem
                projectId={id}
                relatedProjects={relatedProjects}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
              />
            </Col>
          </Row>
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('overview-and-purpose')}>
        <ContentItem>
          <ContentItems
            items={[
              <ProjectPurposePinpointItem
                key="project-purpose"
                projectId={id}
                purpose={purpose}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
              />,
              <ProjectTaskPinpointItem
                key="project-task"
                projectId={id}
                task={task}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem
          {...(isBefore2025 && {
            borderBottom: 0,
            paddingBottom: 0,
          })}
        >
          <ContentItems
            items={[
              <ProjectOverviewPinpointItem
                key="project-overview"
                projectId={id}
                overview={overview}
                overviewUrl={overviewUrl}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
              />,
              ...(isBefore2025
                ? [
                    <Space key="effort-and-result" direction="vertical">
                      <Text variant="h4" text={t('effort-and-result')} />
                      <Text
                        variant="body"
                        text={props.fundAdditionalAttributes?.effortAndResult}
                        weight="regular"
                      />
                    </Space>,
                  ]
                : []),
            ]}
          />
        </ContentItem>
        {!isBefore2025 && (
          <ContentItem borderBottom={0} paddingBottom={0}>
            <Box marginBottom={8}>
              <Text variant="h4" text={t('necessity-of-fund-method')} />
            </Box>
            <ContentItems
              items={[
                <KsFundNecessityTypePinpointItem
                  key="fund-necessity-type"
                  projectId={id}
                  pinpointSheetType={PINPOINT_SHEET_TYPE}
                  necessityType={rest.fundAdditionalAttributes?.necessityType}
                />,
                <KsFundNecessityReasonsPinpointItem
                  key="fund-necessity-reasons-for-local-government"
                  projectId={id}
                  pinpointSheetType={PINPOINT_SHEET_TYPE}
                  necessityReasons={
                    rest.fundAdditionalAttributes?.necessityReasons
                  }
                />,
              ]}
            />
          </ContentItem>
        )}
      </ContentWrapper>

      {!isBefore2025 && (
        <>
          <ContentWrapper title={t('start-fiscal-year-of-project')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <PinpointMarker
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType: PINPOINT_SHEET_TYPE,
                  ksStandard: {
                    field: 'ks.standard.start-year',
                    path: { project: id },
                  },
                })}
              >
                <TitledText
                  title={t('start-fiscal-year-of-project')}
                  text={startYear ?? t('unknown-start')}
                />
              </PinpointMarker>
            </ContentItem>
          </ContentWrapper>
          <ContentWrapper title={t('scheduled-end-time')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <PinpointMarker
                    key="fund-scheduled-end-period"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field: 'ks.standard.end-year',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t('fund-scheduled-end-period')}
                      text={
                        parsedFundAdditionalAttributes.scheduledEnd?.endDate
                      }
                    />
                  </PinpointMarker>,
                ]}
              />
            </ContentItem>
            <ContentItem>
              <ContentItems
                items={[
                  <PinpointMarker
                    key="supplementary-reasons"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field: 'ks.standard.end-year-supplementary-reasons',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t('supplementary-reasons')}
                      text={
                        parsedFundAdditionalAttributes.scheduledEnd
                          ?.supplementaryReasonsForEndPeriod
                      }
                    />
                  </PinpointMarker>,
                  <PinpointMarker
                    key="reason-for-changing-scheduled-end-period"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field:
                          'ks.standard.reason-for-changing-scheduled-end-period',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t('reason-for-changing-scheduled-end-period')}
                      text={
                        parsedFundAdditionalAttributes.scheduledEnd
                          ?.reasonsForChangingScheduledEndPeriod
                      }
                    />
                  </PinpointMarker>,
                ]}
              />
            </ContentItem>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <PinpointMarker
                    key="end-date-of-new-application"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field: 'ks.standard.end-date-of-new-application',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t('end-date-of-new-application')}
                      text={
                        parsedFundAdditionalAttributes.scheduledEnd
                          ?.endDateOfNewApplicationReceiving
                      }
                    />
                  </PinpointMarker>,
                ]}
              />
            </ContentItem>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <PinpointMarker
                    key="supplementary-reasons"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field:
                          'ks.standard.end-date-of-new-application-supplementary-reasons',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t('supplementary-reasons')}
                      text={
                        parsedFundAdditionalAttributes.scheduledEnd
                          ?.supplementaryReasonsForNewApplicationReceiving
                      }
                    />
                  </PinpointMarker>,
                  <PinpointMarker
                    key="reason-for-changing-end-date-of-new-application"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksStandard: {
                        field:
                          'ks.standard.reason-for-changing-end-date-of-new-application',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      title={t(
                        'reason-for-changing-end-date-of-new-application',
                      )}
                      text={
                        parsedFundAdditionalAttributes.scheduledEnd
                          ?.reasonsForChangingEndPeriodOfNewApplicationReceiving
                      }
                    />
                  </PinpointMarker>,
                ]}
              />
            </ContentItem>
          </ContentWrapper>
        </>
      )}

      <ContentWrapper title={t('legal-bases')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ProjectLegalBasesPinpointItem
            projectId={id}
            pinpointSheetType={PINPOINT_SHEET_TYPE}
            legalBases={legalBases ?? []}
          />
          <ProjectPlanPinpointItem
            projectId={id}
            pinpointSheetType={PINPOINT_SHEET_TYPE}
            plans={plans ?? []}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper borderBottom={0} title={t('others')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <ProjectRemarkPinpointItem
                key="remark"
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                remark={remark}
              />,
              <ProjectOldProjectNumberPinpointItem
                key="old-project-number"
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                oldProjectNumber={oldProjectNumber}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
